import mixpanel from 'trackers/mixpanel'
import type { Surface } from 'types/project'

export const SharedEvent = {
  FilterApplied: 'Filter Applied',
  SearchPerformed: 'Search Performed',
  DateRangeChanged: 'Date Range Changed',
  BreakdownChanged: 'Breakdown Changed',
  ExportStarted: 'Export Started',
  ViewSaved: 'View Saved',
  FilterSaved: 'Filter Saved',
  SavedFilterLoaded: 'Saved Filter Loaded',
} as const

export type Location = 'header' | 'sidebar' | 'toolbar' | 'context_menu'

export type FilterFieldType =
  | 'text'
  | 'numeric'
  | 'date'
  | 'rating'
  | 'sentiment'
  | 'custom_field'

export type SearchType = 'lexical' | 'semantic' | 'logical'

export type LogicalSearchOperator =
  | 'include_any'
  | 'include_all'
  | 'exclude_any'
  | 'exclude_all'

export type ExportObjectType = 'chart' | 'reviews' | 'dashboard' | 'themes'

export interface FilterAppliedProps {
  surface: Surface
  location: Location
  filter_field: string
  filter_field_type: FilterFieldType
  value_count: number
  action: 'add' | 'remove' | 'edit' | 'clear_all'
  is_smart_search?: boolean
}

export interface SearchPerformedProps {
  surface: Surface
  location: Location
  search_type: SearchType
  query_text: string
  query_length: number
  n_terms: number
  /** Only set for `search_type: logical`. */
  operator?: LogicalSearchOperator
  /** Only set for `search_type: logical`. */
  search_card_count?: number
  action: 'submit' | 'add' | 'edit' | 'remove' | 'clear'
  has_filters: boolean
  /** Null when fired before the query settles. */
  result_count?: number | null
}

export interface DateRangeChangedProps {
  surface: Surface
  location: Location
  range_type: 'preset' | 'custom'
  /** Human label of the preset. Undefined for custom ranges. */
  preset?: string
  span_days: number
  start_date: string
  end_date: string
}

export interface BreakdownChangedProps {
  surface: Surface
  location: Location
  breakdown_field: string
  breakdown_field_type: 'standard' | 'custom_breakdown'
  breakdown_count: number
  action: 'add' | 'remove' | 'replace' | 'clear'
}

export interface ExportStartedProps {
  surface: Surface
  location: Location
  format: 'png' | 'csv' | 'pdf' | 'xlsx'
  object_type: ExportObjectType
  /** Null for image exports. */
  record_count?: number | null
  chart_type?: string
  panel_index?: number
}

export const trackFilterApplied = (props: FilterAppliedProps): void => {
  mixpanel.track(SharedEvent.FilterApplied, props)
}

export const trackSearchPerformed = (props: SearchPerformedProps): void => {
  mixpanel.track(SharedEvent.SearchPerformed, props)
}

export const trackDateRangeChanged = (props: DateRangeChangedProps): void => {
  mixpanel.track(SharedEvent.DateRangeChanged, props)
}

export const trackBreakdownChanged = (props: BreakdownChangedProps): void => {
  mixpanel.track(SharedEvent.BreakdownChanged, props)
}

export const trackExportStarted = (props: ExportStartedProps): void => {
  mixpanel.track(SharedEvent.ExportStarted, props)
}

export interface ViewSavedProps {
  surface: Surface
  view_name: string
}

export interface FilterSavedProps {
  surface: Surface
  filter_name: string
}

export interface SavedFilterLoadedProps {
  surface: Surface
  filter_name: string
  criteria_count: number
}

export const trackViewSaved = (props: ViewSavedProps): void => {
  mixpanel.track(SharedEvent.ViewSaved, props)
}

export const trackFilterSaved = (props: FilterSavedProps): void => {
  mixpanel.track(SharedEvent.FilterSaved, props)
}

export const trackSavedFilterLoaded = (props: SavedFilterLoadedProps): void => {
  mixpanel.track(SharedEvent.SavedFilterLoaded, props)
}
